import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

// Shared top navigation used on every page
function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';

  // Anchor links: on the home page use in-page #hash, elsewhere jump back to "/#hash"
  const hash = (id) => (isHome ? `#${id}` : `/#${id}`);

  const navLinks = [
    { type: 'anchor', id: 'programs', label: 'Programs' },
    { type: 'route', to: '/after-school', label: 'After-School' },
    { type: 'anchor', id: 'values', label: 'Values' },
    { type: 'anchor', id: 'testimonials', label: 'Testimonials' },
    { type: 'anchor', id: 'faq', label: 'FAQ' },
    { type: 'route', to: '/contact', label: 'Contact' },
  ];

  return (
    <nav className="fixed top-0 w-full bg-slate-900/95 backdrop-blur-lg border-b border-slate-700 z-50" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="flex items-center space-x-3" data-testid="header-logo-link">
            <img
              src="https://customer-assets.emergentagent.com/job_design-inspiration-4/artifacts/f59reduc_logo.jpg"
              alt="Soccer Sphere"
              className="h-16 w-16 object-contain"
            />
            <span className="text-xl font-bold text-white">Soccer Sphere</span>
          </Link>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) =>
              link.type === 'anchor' ? (
                <a
                  key={link.id}
                  href={hash(link.id)}
                  className="text-gray-300 hover:text-white transition"
                  data-testid={`nav-${link.id}-link`}
                >
                  {link.label}
                </a>
              ) : (
                <Link
                  key={link.to}
                  to={link.to}
                  className="text-gray-300 hover:text-white transition"
                  data-testid={`nav-${link.to.replace('/', '')}-link`}
                >
                  {link.label}
                </Link>
              )
            )}
            <a
              href={hash('contact')}
              className="bg-gradient-to-r from-green-700 to-green-600 text-white px-6 py-2 rounded-full hover:opacity-90 transition"
              data-testid="nav-get-started-btn"
            >
              Get Started
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden text-white"
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle-btn"
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="md:hidden bg-slate-900 border-t border-slate-700" data-testid="mobile-menu-panel">
          <div className="px-4 py-4 space-y-3">
            {navLinks.map((link) =>
              link.type === 'anchor' ? (
                <a
                  key={link.id}
                  href={hash(link.id)}
                  onClick={() => setIsMenuOpen(false)}
                  className="block text-gray-300 hover:text-white transition"
                >
                  {link.label}
                </a>
              ) : (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setIsMenuOpen(false)}
                  className="block text-gray-300 hover:text-white transition"
                >
                  {link.label}
                </Link>
              )
            )}
            <Link
              to="/contact"
              onClick={() => setIsMenuOpen(false)}
              className="block bg-gradient-to-r from-green-700 to-green-600 text-white px-6 py-2 rounded-full text-center"
            >
              Get Started
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}

export default Header;
